import { Currency, DateTime, ListViewMenu, ExpenseLockIcon, ListView, ListViewActions } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import ExpenseAmountWidget from '../ExpenseAmountWidget';
import expenseLockedReasons from '../expenseLockedReason';
import DeleteExpenseItemConfirmation from '../DeleteExpenseItemConfirmation';

const SelectionArea = styled.div`
  display: flex;
`;

function ExpenseItemRow({ item, onSaved }) {
  const {
    id,
    vendorName,
    lockStatusId,
    expenseCategory,
    unitAmount,
    unitCount,
    permissions,
    project,
    date,
    isReimbursable,
    isReimbursed,
  } = item;
  const isLocked = lockStatusId !== 'unlocked';
  const history = useHistory();
  const { url } = useRouteMatch();
  const location = useLocation();
  const confirmation = useConfirmation();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const handleOpenItem = (item, readOnly = false) => {
    history.push({
      pathname: readOnly || isLocked ? `${url}/item/${item.id}/view` : `${url}/item/${item.id}/edit`,
      search: location.search,
      state: { scrollToTop: false },
    });
  };

  const handleDelete = async () => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteExpenseItemConfirmation
        id={id}
        onClose={resolve}
        onDelete={async () => {
          await onSaved();
          resolve();
        }}
      />
    ));
    if (!confirm) return;
  };

  const handleReimbursedStatusChange = async (item, flag) => {
    await api.www.workspaces(workspace.id).expenseAdmin(item.id).setReimbursedStatus(flag);
    onSaved();
  };

  const handleClone = async (item) => {
    try {
      await api.www.workspaces(workspace.id).expenseItems(item.id).clone();
      onSaved();
      toast.success('Expense item cloned.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <ListView.Row key={id} onClick={() => handleOpenItem(item)}>
        <ListView.Cell>
          <SelectionArea>
            {isLocked && <ExpenseLockIcon value={lockStatusId} message={expenseLockedReasons[lockStatusId]} />}
          </SelectionArea>
        </ListView.Cell>
        <ListView.Cell>
          <DateTime value={date} />
        </ListView.Cell>
        <ListView.Cell>
          <ListView.CellContent>
            {expenseCategory.name}
            {expenseCategory.unitName && (
              <small>
                {vendorName && `${vendorName} (`}
                {unitCount} x <Currency value={unitAmount} maximumFractionDigits={4} /> per {expenseCategory.unitName}
                {vendorName && ')'}
              </small>
            )}
          </ListView.CellContent>
        </ListView.Cell>
        <ListView.Cell>
          {project && (
            <ListView.CellContent>
              {project.name}
              <small>{project.client.name}</small>
            </ListView.CellContent>
          )}
        </ListView.Cell>
        <ListView.Cell>
          <ExpenseAmountWidget item={item} />
        </ListView.Cell>
        <ListViewActions>
          {isLocked ? (
            <ListViewActions.View onClick={() => handleOpenItem(item, true)} />
          ) : (
            <ListViewActions.Edit onClick={() => handleOpenItem(item)} />
          )}

          <hr />
          <ListViewMenu>
            {({ setIsOpen }) => {
              const handleAction = (action) => setIsOpen(false) || action;

              return (
                <>
                  <ListViewMenu.Item onClick={() => handleOpenItem(item, true)}>View</ListViewMenu.Item>
                  <ListViewMenu.Item
                    disabled={isLocked}
                    tooltip={isLocked ? expenseLockedReasons[lockStatusId] : undefined}
                    onClick={() => handleAction(handleOpenItem(item))}>
                    Edit
                  </ListViewMenu.Item>
                  <ListViewMenu.Item
                    disabled={isLocked}
                    tooltip={isLocked ? expenseLockedReasons[lockStatusId] : undefined}
                    onClick={() => handleAction(handleDelete())}>
                    Delete
                  </ListViewMenu.Item>

                  {permissions.reimburse && (
                    <ListViewMenu.Item
                      disabled={!isReimbursable}
                      tooltip={!isReimbursable ? 'Expense item is not set as reimbursable.' : undefined}
                      onClick={async () => {
                        handleAction(await handleReimbursedStatusChange(item, !isReimbursed));
                      }}>
                      {isReimbursed ? 'Set as Not Reimbursed' : 'Set as Reimbursed'}
                    </ListViewMenu.Item>
                  )}

                  <ListViewMenu.Item
                    onClick={async () => {
                      handleAction(await handleClone(item));
                    }}>
                    Clone
                  </ListViewMenu.Item>
                </>
              );
            }}
          </ListViewMenu>
        </ListViewActions>
      </ListView.Row>
    </>
  );
}

export default ExpenseItemRow;
export { ExpenseItemRow };
